/**
 * The error shape every API namespace returns.
 *
 * The server always answers a failure with a machine-readable `code` beside
 * the human message, because the UI has to branch on the failure and a
 * sentence is not something to branch on. `requestId` is echoed so that an
 * operator reporting a problem can quote one value that appears in the server
 * logs, the trace and the financial record.
 */
export interface ApiErrorBody {
  error: {
    retry_after?: number
    code: string
    message: string
    fields?: Record<string, string[]>
  }
  request_id: string | null
}

export class ApiError extends Error {
  readonly retryAfter: number | undefined
  readonly status: number
  readonly code: string
  readonly requestId: string | null
  readonly fields: Record<string, string[]> | undefined

  constructor(status: number, body: Partial<ApiErrorBody> | null, fallback: string, retryHeader?: string | null) {
    const message = body?.error?.message ?? fallback
    super(message)
    this.name = 'ApiError'
    const seconds = Number(retryHeader ?? body?.error?.retry_after)
    this.retryAfter = Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : undefined
    this.status = status
    this.code = body?.error?.code ?? 'unknown_error'
    this.requestId = body?.request_id ?? null
    this.fields = body?.error?.fields
  }

  /** True when signing in again is the thing that would fix it. */
  get isAuthenticationFailure(): boolean {
    return this.status === 401
  }
}
